// FILE: apps/admin/src/pages/BalanceAdjust.tsx
// VERSION: 2.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Dedicated operator console for Step-Up guarded RUB/GRAM player balance adjustments via UserPicker and atomic RUB/GRAM self-topup for operator accounts.
//   SCOPE: BalanceAdjust component.
//   DEPENDS: ../api.js, ../components/ConfirmDialog.js, ../components/Icons.js, ../components/UserAvatar.js, ../components/UserPicker.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   BalanceAdjust - Operator console for Step-Up RUB/GRAM player balance adjustments and atomic RUB/GRAM self-topup
// END_MODULE_MAP

import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api, gram, rub } from '../api.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { AdminIcon } from '../components/Icons.js';
import { resolveAdminUserDisplayName } from '../components/UserAvatar.js';
import { type PickedAdminUser, UserPicker } from '../components/UserPicker.js';
import { useToast } from '../toast.js';

interface SelfBalanceResponse {
  ok: boolean;
  userId?: string;
  currency?: 'RUB' | 'GRAM';
  balanceKopecks?: number;
  gramBalanceMilli?: number;
}

const SELF_RUB_PRESETS = [500, 2000, 5000] as const;
const SELF_GRAM_PRESETS = [10, 50, 250] as const;

// START_CONTRACT: BalanceAdjust
//   PURPOSE: Render the dedicated Balance Adjustment & Self-Topup console with UserPicker search and Step-Up OTP protection.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Calls POST /admin/actions/execute (balance.adjust / balance.gram_adjust) and POST /admin/me/balance (RUB / GRAM)
//   LINKS: M-ADMIN
// END_CONTRACT: BalanceAdjust
export function BalanceAdjust() {
  // START_BLOCK_BALANCE_ADJUST
  const qc = useQueryClient();
  const toast = useToast();

  const [targetUserId, setTargetUserId] = useState('');
  const [pickedUser, setPickedUser] = useState<PickedAdminUser | null>(null);
  const [currency, setCurrency] = useState<'RUB' | 'GRAM'>('RUB');
  const [amount, setAmount] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [selfCurrency, setSelfCurrency] = useState<'RUB' | 'GRAM'>('RUB');
  const [selfAmount, setSelfAmount] = useState('500');
  const [selfReason, setSelfReason] = useState('Тестовое пополнение баланса оператора');
  const [selfBusy, setSelfBusy] = useState(false);

  const handleSelfCurrencyChange = (next: 'RUB' | 'GRAM') => {
    setSelfCurrency(next);
    setSelfAmount(next === 'GRAM' ? '50' : '500');
  };

  const handleConfirm = async ({
    reason,
    stepUpGrantToken,
  }: {
    reason: string;
    stepUpGrantToken?: string;
  }) => {
    const val = parseFloat(amount.replace(',', '.'));
    if (!Number.isFinite(val) || val === 0) {
      throw new Error('Введите ненулевую сумму');
    }

    if (currency === 'GRAM') {
      await api.post('/admin/actions/execute', {
        kind: 'balance.gram_adjust',
        targetId: targetUserId.trim(),
        payload: { deltaMilliGram: Math.round(val * 1000) },
        reason,
        stepUpGrantToken,
      });
    } else {
      await api.post('/admin/actions/execute', {
        kind: 'balance.adjust',
        targetId: targetUserId.trim(),
        payload: { deltaKopecks: Math.round(val * 100) },
        reason,
        stepUpGrantToken,
      });
    }

    setConfirmOpen(false);
    setAmount('');
    void qc.invalidateQueries({ queryKey: ['user-picker'] });
    void qc.invalidateQueries({ queryKey: ['admin-treasury'] });
    toast(
      `Баланс ${currency} пользователя успешно скорректирован (${val > 0 ? '+' : ''}${val} ${currency === 'GRAM' ? 'GRAM' : '₽'})`,
    );
  };

  const handleSelfTopup = async () => {
    const val = parseFloat(selfAmount.replace(',', '.'));
    if (!Number.isFinite(val) || val === 0) {
      toast(`Введите ненулевую сумму в ${selfCurrency === 'GRAM' ? 'GRAM' : '₽'}`);
      return;
    }

    const effectiveReason =
      selfReason.trim() ||
      (selfCurrency === 'GRAM'
        ? 'Пополнение GRAM оператором себе'
        : 'Пополнение баланса оператором себе');

    setSelfBusy(true);
    try {
      const res = await api.post<SelfBalanceResponse>(
        '/admin/me/balance',
        selfCurrency === 'GRAM'
          ? {
              currency: 'GRAM',
              deltaMilliGram: Math.round(val * 1000),
              reason: effectiveReason,
            }
          : {
              currency: 'RUB',
              deltaKopecks: Math.round(val * 100),
              reason: effectiveReason,
            },
      );

      void qc.invalidateQueries({ queryKey: ['user-picker'] });
      void qc.invalidateQueries({ queryKey: ['admin-treasury'] });

      if (selfCurrency === 'GRAM') {
        const newBal =
          typeof res?.gramBalanceMilli === 'number'
            ? ` · Новый баланс: ${gram(res.gramBalanceMilli)} GRAM`
            : '';
        toast(
          `На ваш профиль начислено ${val > 0 ? '+' : ''}${val} GRAM${newBal} (записано в леджер без DRIFT)`,
        );
      } else {
        const newBal =
          typeof res?.balanceKopecks === 'number'
            ? ` · Новый баланс: ${rub(res.balanceKopecks)} ₽`
            : '';
        toast(
          `На ваш профиль начислено ${val > 0 ? '+' : ''}${val} ₽${newBal} (записано в леджер без DRIFT)`,
        );
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Ошибка начисления на свой баланс');
    } finally {
      setSelfBusy(false);
    }
  };

  const pickedSummaryLabel = pickedUser
    ? (() => {
        const d = resolveAdminUserDisplayName(pickedUser);
        const handle = pickedUser.username ? `@${pickedUser.username}` : `TG #${pickedUser.telegramId}`;
        return `${d.primary} (${handle}, ID ${pickedUser.id.slice(0, 8)}…)`;
      })()
    : targetUserId.trim();

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Корректировка баланса (RUB &amp; GRAM)</h1>
          <div className="page-subtitle">
            Ручное начисление или списание средств через единый атомарный леджер без возникновения DRIFT с защитой Step-Up OTP
          </div>
        </div>
      </div>

      <div className="grid cols-2">
        {/* Left Card: Step-Up Player Balance Adjustment */}
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Изменить баланс игрока (Step-Up)</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <UserPicker
              value={targetUserId}
              onChange={(val, u) => {
                setTargetUserId(val);
                setPickedUser(u ?? null);
              }}
              label="Пользователь (поиск по @username, имени, TG ID или выбор из списка) *"
            />

            <div className="grid cols-2">
              <div>
                <label className="field-label">Валюта леджера</label>
                <select
                  style={{ width: '100%' }}
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value as 'RUB' | 'GRAM')}
                >
                  <option value="RUB">₽ RUB (рубли)</option>
                  <option value="GRAM">GRAM (внутриигровая валюта)</option>
                </select>
              </div>
              <div>
                <label className="field-label">
                  Сумма ({currency === 'GRAM' ? 'GRAM' : '₽'}, + начисление / − списание)
                </label>
                <input
                  className="mono"
                  style={{ width: '100%' }}
                  placeholder={currency === 'GRAM' ? 'Например: 25 или -10' : 'Например: 250 или -100'}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
            </div>

            <div className="row" style={{ justifyContent: 'flex-end', marginTop: 4 }}>
              <button
                type="button"
                className="btn primary"
                disabled={!targetUserId.trim() || !amount.trim()}
                onClick={() => setConfirmOpen(true)}
              >
                <AdminIcon name="lock" size={14} />
                <span>Продолжить с подтверждением Step-Up →</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Card: Operator Self-Topup (RUB & GRAM) */}
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Быстрое пополнение своего аккаунта оператора</h2>
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
            Моментальное начисление RUB или GRAM на собственный профиль в Mini App через атомарный леджер для проверки покупок Stars, кейсов и игры Crash.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="grid cols-2">
              <div>
                <label className="field-label">Валюта пополнения</label>
                <select
                  style={{ width: '100%' }}
                  value={selfCurrency}
                  onChange={(e) => handleSelfCurrencyChange(e.target.value as 'RUB' | 'GRAM')}
                >
                  <option value="RUB">₽ RUB (рубли)</option>
                  <option value="GRAM">GRAM (внутриигровая валюта)</option>
                </select>
              </div>
              <div>
                <label className="field-label">
                  Сумма пополнения ({selfCurrency === 'GRAM' ? 'GRAM' : '₽'})
                </label>
                <input
                  className="mono"
                  style={{ width: '100%' }}
                  value={selfAmount}
                  onChange={(e) => setSelfAmount(e.target.value)}
                />
              </div>
            </div>

            <div className="row" style={{ gap: 6 }}>
              <span className="muted" style={{ fontSize: 12 }}>
                Быстрый выбор:
              </span>
              {(selfCurrency === 'GRAM' ? SELF_GRAM_PRESETS : SELF_RUB_PRESETS).map((preset) => {
                const presetStr = String(preset);
                const isActive = selfAmount.trim() === presetStr;
                return (
                  <button
                    key={preset}
                    type="button"
                    className={`cat-preset-btn mono ${isActive ? 'active' : ''}`}
                    style={{
                      border: '1px solid var(--hair)',
                      padding: '4px 10px',
                    }}
                    onClick={() => setSelfAmount(presetStr)}
                  >
                    +{preset.toLocaleString('ru-RU')} {selfCurrency === 'GRAM' ? 'GRAM' : '₽'}
                  </button>
                );
              })}
            </div>

            <div>
              <label className="field-label">Комментарий в леджер</label>
              <input
                style={{ width: '100%' }}
                value={selfReason}
                onChange={(e) => setSelfReason(e.target.value)}
              />
            </div>

            <div className="row" style={{ justifyContent: 'flex-end', marginTop: 4 }}>
              <button
                type="button"
                className="btn success"
                disabled={selfBusy}
                onClick={() => void handleSelfTopup()}
              >
                <AdminIcon name={selfCurrency === 'GRAM' ? 'gram' : 'bolt'} size={14} />
                <span>
                  {selfBusy
                    ? 'Начисление…'
                    : `Начислить себе (${selfCurrency === 'GRAM' ? 'GRAM' : '₽ RUB'})`}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title={`Корректировка баланса (${amount} ${currency === 'GRAM' ? 'GRAM' : '₽'})`}
        description={`Целевой пользователь: ${pickedSummaryLabel}`}
        confirmLabel="Подтвердить и провести в леджере"
        stepUpAction="balance.admin_adjust"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />
    </>
  );
  // END_BLOCK_BALANCE_ADJUST
}
